import classNames from "classnames";
import pluralize from "pluralize";
import {type ReactNode, useEffect, useMemo, useState} from "react";

import {Chip} from "@/components/chip/Chip.tsx";
import {NavTabItem} from "@/components/navTabs/NavTabs.types.ts";
import {
  ComponentStatus,
  Incident,
  IncidentType as IncidentTypeEnum,
  StatusPage,
  StatusPageLayoutPreset,
} from "@/lib/types/statuspages.ts";

import {CurrentStatusTabType, HistoryTabType, type PublicStatusPageMetric} from "../../types";
import {EmptyEventCard} from "../shared/EmptyEventCard";
import {StatusPageStatusBar} from "../StatusPageStatusBar/StatusPageStatusBar.tsx";
import type {StatusPageStatusBarProps} from "../StatusPageStatusBar/StatusPageStatusBar.types.ts";
import {SystemMetrics} from "../systemmetrics/SystemMetrics";
import {ComponentHistoryTable} from "./components/ComponentHistoryTable";
import {ComponentList} from "./components/ComponentList";
import {IncidentsList} from "./components/IncidentsList";
import {SortMenu} from "./components/SortMenu";
import {StatusTabs} from "./components/StatusTabs";
import {COMPONENT_SORT_OPTIONS, ComponentSortOptions, sortComponents} from "./utils/sortComponents";
import {
  INCIDENTS_SORT_OPTIONS,
  SCHEDULED_MAINTENANCE_SORT_OPTIONS,
  SortOptions,
  sortIncidents,
} from "./utils/sortIncidents";

import styles from "./CurrentStatusOverview.module.scss";

const ALL_TABS: NavTabItem[] = [
  {id: CurrentStatusTabType.OVERVIEW, title: "Overview"},
  {id: CurrentStatusTabType.ACTIVE_INCIDENTS, title: "Active Incidents"},
  {id: CurrentStatusTabType.COMPONENT_STATUS, title: "Component Status"},
  {id: CurrentStatusTabType.ACTIVE_MAINTENANCE, title: "Active Maintenance"},
  {id: CurrentStatusTabType.SCHEDULED_MAINTENANCE, title: "Scheduled Maintenance"},
];

export interface CurrentStatusOverviewProps {
  statusPage: StatusPage;
  componentStatuses: ComponentStatus[];
  activeTab?: CurrentStatusTabType;
  onTabChange?: (tab: CurrentStatusTabType) => void;
  onSwitchToHistory?: (tab: HistoryTabType) => void;
  // Whether the History Past Incidents / Past Maintenance sub-tabs are reachable — gates the
  // "View Past …" empty-state links so we never link to a hidden tab or sub-tab.
  canViewPastIncidents?: boolean;
  canViewPastMaintenance?: boolean;
  metrics?: PublicStatusPageMetric[];
  mode?: "public" | "private";
  slug?: string;
  pageId?: number;
  statusBarProps?: StatusPageStatusBarProps | null;
  variant?: "default" | "inspire";
  componentLinkTo?: (serviceId: number) => string;
  layout?: StatusPageLayoutPreset;
}

export const CurrentStatusOverview = ({
  statusPage,
  componentStatuses,
  activeTab: controlledActiveTab,
  onTabChange,
  onSwitchToHistory,
  canViewPastIncidents = true,
  canViewPastMaintenance = true,
  metrics,
  mode,
  slug,
  pageId,
  statusBarProps,
  variant = "default",
  componentLinkTo,
  layout = StatusPageLayoutPreset.EXPANDED,
}: CurrentStatusOverviewProps) => {
  const [incidentsSortBy, setIncidentsSortBy] = useState<SortOptions>("most_recently_created");
  const [maintenanceSortBy, setMaintenanceSortBy] = useState<SortOptions>("most_recently_created");
  const [scheduledSortBy, setScheduledSortBy] = useState<SortOptions>("most_recently_updated");
  const [allEventsSortBy, setAllEventsSortBy] = useState<SortOptions>("most_recently_created");
  const [componentSortBy, setComponentSortBy] = useState<ComponentSortOptions>("default");
  const [internalActiveTab, setInternalActiveTab] = useState<CurrentStatusTabType>(CurrentStatusTabType.OVERVIEW);

  const activeTab = controlledActiveTab ?? internalActiveTab;
  const setActiveTab = onTabChange ?? setInternalActiveTab;

  // Active incidents (INCIDENT type)
  const activeIncidents: Incident[] = (statusPage.active_incidents || []).filter(
    (i) => i.incident_type === IncidentTypeEnum.INCIDENT
  );

  // Active maintenance (SCHEDULED_MAINTENANCE type that is currently active)
  const activeMaintenance: Incident[] = (statusPage.active_incidents || []).filter(
    (i) => i.incident_type === IncidentTypeEnum.SCHEDULED_MAINTENANCE
  );

  // Scheduled (upcoming) maintenance
  const scheduledMaintenance: Incident[] = statusPage.upcoming_maintenance || [];

  const hideEmptyTabs = !!statusPage.hide_empty_tabs_status;
  // Not gated on show_summary_metrics: that switch is the History tab's global uptime / outages /
  // downtime KPI row, as its description says. Legacy renders these charts for any page with
  // visible metrics.
  const hasSystemMetrics = !!metrics && metrics.length > 0;

  const visibleTabs = (() => {
    const tabs = ALL_TABS.filter((tab) => {
      if (tab.id === CurrentStatusTabType.COMPONENT_HISTORY) return !!statusPage.show_component_history;
      // "Display Active Incidents & Maintenance" (show_active_incidents) gates the active
      // incidents tab and both maintenance tabs together.
      if (
        tab.id === CurrentStatusTabType.ACTIVE_INCIDENTS ||
        tab.id === CurrentStatusTabType.ACTIVE_MAINTENANCE ||
        tab.id === CurrentStatusTabType.SCHEDULED_MAINTENANCE
      ) {
        if (!statusPage.show_active_incidents) return false;
        // Hide individual event sub-tabs when empty and hide_empty_tabs_status is active
        if (hideEmptyTabs) {
          if (tab.id === CurrentStatusTabType.ACTIVE_INCIDENTS) return activeIncidents.length > 0;
          if (tab.id === CurrentStatusTabType.ACTIVE_MAINTENANCE) return activeMaintenance.length > 0;
          if (tab.id === CurrentStatusTabType.SCHEDULED_MAINTENANCE) return scheduledMaintenance.length > 0;
        }
        return true;
      }
      if (tab.id === CurrentStatusTabType.COMPONENT_STATUS) return variant === "inspire";
      return true;
    });
    // With a single content tab the Overview duplicates it, so drop the redundant Overview tab.
    // Skip this when tabs were dynamically hidden by empty state: the Overview is the fallback
    // landing view that still shows the component list — the only place components render in the
    // default variant (the inspire variant also shows the combined events section there) — so
    // dropping it could hide that content once individual event tabs are gone.
    // System Metrics render below the Overview only, so a page carrying metrics is never
    // duplicating its single content tab — dropping the Overview there force-switched the landing
    // tab (inspire + active incidents off) and took the whole System Metrics section with it.
    if (!hideEmptyTabs && !hasSystemMetrics) {
      const contentTabs = tabs.filter((tab) => tab.id !== CurrentStatusTabType.OVERVIEW);
      if (contentTabs.length <= 1) return contentTabs;
    }
    return tabs;
  })();

  // Keep the active tab valid when the Overview tab is dropped.
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0].id as CurrentStatusTabType);
    }
  }, [visibleTabs, activeTab, setActiveTab]);

  const sortedIncidents = sortIncidents(activeIncidents, incidentsSortBy);
  const sortedMaintenance = sortIncidents(activeMaintenance, maintenanceSortBy);
  const sortedScheduled = sortIncidents(scheduledMaintenance, scheduledSortBy);
  const allEvents = sortIncidents([...(statusPage.active_incidents || []), ...scheduledMaintenance], allEventsSortBy);

  // Create component status lookup. Memoized so the ~1000 memoized ComponentItem rows keep stable
  // prop identities across re-renders (sort menu, dark-mode toggle, auto-refresh revalidation).
  const componentStatusLookup = useMemo(
    () =>
      componentStatuses.reduce<Record<string, ComponentStatus>>((acc, status) => {
        acc[status.value] = status;
        return acc;
      }, {}),
    [componentStatuses]
  );

  const componentStatusRank = useMemo(
    () =>
      componentStatuses.reduce<Record<string, number>>((acc, status) => {
        acc[status.value] = status.rank;
        return acc;
      }, {}),
    [componentStatuses]
  );

  const componentCount = statusPage.components?.length || 0;

  // Sort components
  const sortedComponents = useMemo(
    () => sortComponents(statusPage.components || [], componentSortBy, componentStatusRank),
    [statusPage.components, componentSortBy, componentStatusRank]
  );

  // The compact layout has no room for per-component response time graphs, so they are
  // always hidden there regardless of the saved setting (the design tab disables the toggle
  // under compact, but existing pages may still carry it enabled).
  const showResponseTime = statusPage.show_component_response_time && layout !== StatusPageLayoutPreset.COMPACT;

  const tabContent: Record<CurrentStatusTabType, ReactNode> = {
    [CurrentStatusTabType.OVERVIEW]: (
      <>
        {variant === "inspire" && statusPage.show_active_incidents && (
          <div className={styles.sectionBlock}>
            <IncidentsList
              incidents={allEvents}
              title="All Events"
              sortBy={allEventsSortBy}
              onSortChange={setAllEventsSortBy}
              componentStatusRank={componentStatusRank}
              sortOptions={INCIDENTS_SORT_OPTIONS}
              emptyMessage="There are no active Events at this time."
              timeFormat={statusPage.time_format}
              timezone={statusPage.used_timezone}
              layout={layout}
              variant={variant}
            />
          </div>
        )}
        <div className={styles.sectionBlock}>
          <div className={styles.sectionHeader}>
            <div className={styles.titleRow}>
              <h4 className={styles.subsectionTitle}>
                {variant === "inspire" ? "Component Status" : "Current Status"}
              </h4>
              <Chip variant="count" size="tiny" aria-label={pluralize("Component", componentCount, true)}>
                {componentCount}
              </Chip>
            </div>
            <div className={styles.sortMenuWrapper}>
              <SortMenu
                sortBy={componentSortBy}
                onSortChange={setComponentSortBy}
                options={COMPONENT_SORT_OPTIONS}
                variant={variant}
              />
            </div>
          </div>
          {componentCount > 0 ? (
            <ComponentList
              components={sortedComponents}
              componentStatusLookup={componentStatusLookup}
              componentStatusRank={componentStatusRank}
              showResponseTime={showResponseTime}
              showComponentDescriptions={statusPage.show_component_descriptions}
              timezone={statusPage.used_timezone}
              timeFormat={statusPage.time_format}
              componentLinkTo={statusPage.allow_drill_down ? componentLinkTo : undefined}
              layout={layout}
              variant={variant}
            />
          ) : (
            <div className={styles.emptyContainer}>
              <EmptyEventCard message="There are no Components." />
            </div>
          )}
        </div>
      </>
    ),
    [CurrentStatusTabType.ACTIVE_INCIDENTS]: (
      <div className={styles.sectionBlock}>
        <IncidentsList
          incidents={sortedIncidents}
          sortBy={incidentsSortBy}
          onSortChange={setIncidentsSortBy}
          componentStatusRank={componentStatusRank}
          sortOptions={INCIDENTS_SORT_OPTIONS}
          emptyMessage="There are no active Incidents at this time."
          emptyCtaLabel={onSwitchToHistory && canViewPastIncidents ? "View Past Incidents" : undefined}
          onEmptyCtaClick={
            onSwitchToHistory && canViewPastIncidents
              ? () => onSwitchToHistory(HistoryTabType.PAST_INCIDENTS)
              : undefined
          }
          timeFormat={statusPage.time_format}
          timezone={statusPage.used_timezone}
          layout={layout}
          variant={variant}
        />
      </div>
    ),
    [CurrentStatusTabType.ACTIVE_MAINTENANCE]: (
      <div className={styles.sectionBlock}>
        <IncidentsList
          incidents={sortedMaintenance}
          sortBy={maintenanceSortBy}
          onSortChange={setMaintenanceSortBy}
          componentStatusRank={componentStatusRank}
          sortOptions={INCIDENTS_SORT_OPTIONS}
          emptyMessage="There are no active Maintenance schedules at this time."
          emptyCtaLabel={onSwitchToHistory && canViewPastMaintenance ? "View Past Maintenance" : undefined}
          onEmptyCtaClick={
            onSwitchToHistory && canViewPastMaintenance
              ? () => onSwitchToHistory(HistoryTabType.PAST_MAINTENANCE)
              : undefined
          }
          timeFormat={statusPage.time_format}
          timezone={statusPage.used_timezone}
          layout={layout}
          variant={variant}
        />
      </div>
    ),
    [CurrentStatusTabType.SCHEDULED_MAINTENANCE]: (
      <div className={styles.sectionBlock}>
        <IncidentsList
          incidents={sortedScheduled}
          sortBy={scheduledSortBy}
          onSortChange={setScheduledSortBy}
          componentStatusRank={componentStatusRank}
          sortOptions={SCHEDULED_MAINTENANCE_SORT_OPTIONS}
          emptyMessage="There are no scheduled Maintenance events."
          emptyCtaLabel="View Active Maintenance"
          onEmptyCtaClick={() => setActiveTab(CurrentStatusTabType.ACTIVE_MAINTENANCE)}
          timeFormat={statusPage.time_format}
          timezone={statusPage.used_timezone}
          layout={layout}
          variant={variant}
        />
      </div>
    ),
    [CurrentStatusTabType.COMPONENT_HISTORY]: (
      <div className={styles.sectionBlock}>
        <ComponentHistoryTable />
      </div>
    ),
    [CurrentStatusTabType.COMPONENT_STATUS]: (
      <div className={styles.sectionBlock}>
        <div className={`${styles.sectionHeader} ${styles.sectionHeaderNoTitle}`}>
          <div>
            <div className={styles.componentCount}>{componentCount} Components</div>
          </div>
          <div className={styles.sortMenuWrapper}>
            <SortMenu
              sortBy={componentSortBy}
              onSortChange={setComponentSortBy}
              options={COMPONENT_SORT_OPTIONS}
              variant={variant}
            />
          </div>
        </div>
        {componentCount > 0 ? (
          <ComponentList
            components={sortedComponents}
            componentStatusLookup={componentStatusLookup}
            componentStatusRank={componentStatusRank}
            showResponseTime={showResponseTime}
            showComponentDescriptions={statusPage.show_component_descriptions}
            timezone={statusPage.used_timezone}
            timeFormat={statusPage.time_format}
            componentLinkTo={statusPage.allow_drill_down ? componentLinkTo : undefined}
            layout={layout}
            variant={variant}
          />
        ) : (
          <div className={styles.emptyContainer}>
            <EmptyEventCard message="There are no Components." />
          </div>
        )}
      </div>
    ),
  };

  const showSystemMetrics = hasSystemMetrics && mode && activeTab === CurrentStatusTabType.OVERVIEW;

  return (
    <div className={styles.currentStatusOverview}>
      {statusBarProps && <StatusPageStatusBar {...statusBarProps} enableMobileScroll={variant === "inspire"} />}
      <div className={classNames(styles.whiteBlock, {[styles.whiteBlockFlat]: variant === "inspire"})}>
        {visibleTabs.length > 1 && (
          <StatusTabs
            tabs={visibleTabs}
            activeTab={activeTab}
            onTabChange={(tab) => setActiveTab(tab as CurrentStatusTabType)}
          />
        )}
        <div className={`${styles.whiteBlockContent} ${showSystemMetrics ? "" : styles.whiteBlockContentPadded}`}>
          {tabContent[activeTab]}
        </div>

        {showSystemMetrics && (
          <div className={styles.systemMetricsBlock}>
            <SystemMetrics
              metrics={metrics ?? []}
              mode={mode}
              slug={slug}
              pageId={pageId}
              timezone={statusPage.used_timezone}
              layout={layout}
              variant={variant}
            />
          </div>
        )}
      </div>
    </div>
  );
};
